// Shared helpers for resolving the saved sink topic value on edit-mode load.
//
// Two blocks in DestinationSetup classify a saved topic value into either
// "pick an existing topic" or "create a new topic" mode: one runs on initial
// form load, the other runs once /probe/tree returns. Both must agree, and
// both must handle the Pub/Sub case where the format of `sink_config.topic`
// (e.g. `projects/<p>/topics/foo`) may differ from the format of items in
// the `topics` array (which may be short names). Kafka, Confluent, and JMS
// values never match the GCP path pattern, so they fall through to direct
// equality.

const PUBSUB_RESOURCE_PATH = /^projects\/[^/]+\/(?:topics|subscriptions)\/(.+)$/;

export type SinkConfigField = { name: string; [k: string]: unknown };

/**
 * Pulls the trailing identifier out of a GCP Pub/Sub resource path
 * (`projects/<project>/topics/<name>` or `.../subscriptions/<name>`).
 * Returns the input unchanged when it does not match the pattern, so
 * Kafka/Confluent/JMS values pass through to plain equality.
 */
export function extractTopicIdentifier(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const match = value.match(PUBSUB_RESOURCE_PATH);
  return match ? match[1] : value;
}

/**
 * Format-tolerant membership check. For non-Pub/Sub values this is a plain
 * equality check; for Pub/Sub the trailing topic/subscription name is
 * compared so a stored full path matches a short name in the list (and
 * vice versa).
 */
export function topicListContains(value: unknown, list: unknown[] | null | undefined): boolean {
  if (!Array.isArray(list) || list.length === 0) return false;
  if (value == null || value === '') return false;
  const id = extractTopicIdentifier(value);
  return list.some((item) => extractTopicIdentifier(item) === id);
}

/**
 * Returns the sink_config key the picker writes to for a given connector
 * schema. Mirrors `KafkaSinkConfig._getTopicFieldName`.
 *
 *   JMS                                → 'target.name'
 *   Kafka / Confluent Kafka / Pub/Sub  → 'topic'
 */
export function resolveSinkTopicFieldName(
  sinkConfigFields: SinkConfigField[] | null | undefined,
): string {
  if (!Array.isArray(sinkConfigFields)) return 'topic';
  const hasField = (name: string) => sinkConfigFields.some((f) => f && f.name === name);
  if (hasField('target.name')) return 'target.name';
  if (hasField('topic')) return 'topic';
  return 'topic';
}

export type SavedTopicClassification =
  | { status: 'exists'; topicFieldName: string; value: string }
  | { status: 'new'; topicFieldName: string; value: string }
  | { status: 'unknown'; topicFieldName: string; value: string | null };

/**
 * Classifies the saved sink-config topic value against the topics and queues
 * lists from `/probe/tree`. Returns:
 *
 *   `exists` — the saved value matches an entry in topics OR queues. Caller
 *              should pre-fill `selectedTopic` (pick-existing mode).
 *   `new`    — both lists have loaded and neither contains the saved value.
 *              Caller should set `isNewTopicChecked: true` and pre-fill
 *              `newTopicName` so the user sees what's currently saved.
 *   `unknown` — saved value is empty, OR neither list has loaded yet. The
 *               caller should not touch the mode fields; the probe-arrives
 *               reconciler will classify once data lands.
 */
export function classifySavedTopic(
  sinkConfig: Record<string, unknown> | null | undefined,
  sinkConfigFields: SinkConfigField[] | null | undefined,
  topics: unknown[] | null | undefined,
  queues: unknown[] | null | undefined,
): SavedTopicClassification {
  const topicFieldName = resolveSinkTopicFieldName(sinkConfigFields);
  const rawValue = sinkConfig ? sinkConfig[topicFieldName] : null;
  const value = typeof rawValue === 'string' ? rawValue : null;

  if (!value) {
    return { status: 'unknown', topicFieldName, value: null };
  }

  if (topicListContains(value, topics ?? null) || topicListContains(value, queues ?? null)) {
    return { status: 'exists', topicFieldName, value };
  }

  // Distinguish "lists loaded but missing" from "lists not loaded yet". Only
  // commit to `new` once at least one list has actually loaded; otherwise the
  // probe-arrives reconciler will re-classify when data arrives.
  const topicsLoaded = Array.isArray(topics);
  const queuesLoaded = Array.isArray(queues);
  if (topicsLoaded || queuesLoaded) {
    return { status: 'new', topicFieldName, value };
  }

  return { status: 'unknown', topicFieldName, value };
}
